import { Button } from '@/components/ui/button'
import { Radio, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { liveInterviewLivePath, liveInterviewSignalLabel } from './live-interview-signal.util'
import { useActiveLiveInterview } from './useActiveLiveInterview'

export default function LiveInterviewGlobalBanner() {
    const { bannerSession, activeSessions } = useActiveLiveInterview()
    const [dismissedSessionId, setDismissedSessionId] = useState<string | null>(null)

    const visibleSession = useMemo(() => {
        if (!bannerSession) return null
        if (dismissedSessionId === bannerSession.sessionId) return null
        return bannerSession
    }, [bannerSession, dismissedSessionId])

    if (!visibleSession) return null

    const livePath = liveInterviewLivePath(visibleSession.sessionId)
    const signalLabel = liveInterviewSignalLabel(visibleSession)
    const subjectLabel = visibleSession.profileSubjectName?.trim()
    const title = visibleSession.title?.trim() || 'Live interview'

    return (
        <div className='sticky top-0 z-30 border-b border-emerald-200 bg-emerald-50 px-4 py-2.5 sm:px-6'>
            <div className='mx-auto flex max-w-[1600px] flex-col gap-2 sm:flex-row sm:items-center'>
                <div className='flex min-w-0 flex-1 items-start gap-3'>
                    <span className='mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white'>
                        <Radio className='h-3.5 w-3.5 animate-pulse' />
                    </span>
                    <div className='min-w-0'>
                        <p className='text-sm font-semibold text-emerald-950'>
                            Live interview in progress
                            {activeSessions.length > 1 ? ` · ${activeSessions.length} active` : ''}
                        </p>
                        <p className='truncate text-sm text-emerald-900/90'>
                            {title}
                            {subjectLabel ? ` · ${subjectLabel}` : ''} — {signalLabel}
                        </p>
                    </div>
                </div>
                <div className='flex shrink-0 items-center gap-2 pl-9 sm:pl-0'>
                    <Button asChild size='sm' variant='primary'>
                        <Link to={livePath}>Open Live tab</Link>
                    </Button>
                    <button
                        type='button'
                        className='rounded-md p-1 text-emerald-800/70 hover:bg-emerald-100 hover:text-emerald-950'
                        aria-label='Dismiss live interview banner'
                        onClick={() => setDismissedSessionId(visibleSession.sessionId)}
                    >
                        <X className='h-4 w-4' />
                    </button>
                </div>
            </div>
        </div>
    )
}
